import { z } from 'zod';
import {
  CurrencyCode,
  Instant,
  Latitude,
  Longitude,
  MinorUnitsNonNegative,
  Ok,
  PhoneE164,
  PublicId,
  Uuid,
} from '../common';
import { BakeryStorefrontMode, PartnerOrgType } from '../enums';
import { defineEndpoint } from '../http';
import { patchSchema } from '../patch';

// ---------------------------------------------------------------------------
// Pricing and capacity caps (PRD OPS-005, PAY-023, CAT-014)
// ---------------------------------------------------------------------------

/**
 * Every ceiling the platform enforces, administered here and nowhere else:
 * OPS-005 requires admin-set caps with NO hardcoded values, so each of these is
 * a stored configuration row the engines read, never a constant in code. Rates
 * themselves are administered under OPS-015 (`/internal/rates`); this is the capacity
 * and basket side of the same screen.
 */
export const OperationalCaps = z.object({
  currency: CurrencyCode,
  /** Maximum number of items in one basket. */
  maxBasketItems: z.int().positive().max(10_000),
  /** Maximum value of one basket, integer minor units. */
  maxBasketValue: MinorUnitsNonNegative,
  /** Minimum basket below which checkout is refused (CAT-014). */
  minimumBasket: MinorUnitsNonNegative,
  /** Below this net threshold a small-order fee applies; null = off (PAY-023). */
  smallOrderFeeThreshold: MinorUnitsNonNegative.nullable(),
  smallOrderFee: MinorUnitsNonNegative.nullable(),
  /** Orders accepted per delivery window, platform-wide (RTE-008). */
  maxOrdersPerWindow: z.int().positive().max(100_000),
  /** Orders one bakery location may be allocated in a service day (BAK-011). */
  maxOrdersPerBakeryPerDay: z.int().positive().max(100_000),
  /**
   * RTE-027 (founder, 2026-09-24: "Block Guest checkout if any order quote is
   * above 25 EUR"): the most a courier may quote for one order's delivery, VAT
   * included, integer minor units. ENFORCED - the guest review and checkout
   * refuse above it; a quote exactly at it passes. Positive (zero would refuse
   * every order) and at most EUR 1 000.
   */
  maxCourierQuote: z.int().positive().max(100_000),
  updatedAt: Instant.nullable(),
  updatedBy: z.string().max(160).nullable(),
});
export type OperationalCaps = z.infer<typeof OperationalCaps>;

export const getOperationalCaps = defineEndpoint({
  method: 'GET',
  path: '/internal/caps',
  mount: 'internal',
  summary: 'Admin-set basket, window and bakery ceilings in force; no hardcoded values (OPS-005)',
  response: OperationalCaps,
});

/** The administered ceilings without the read-only envelope fields. */
export const OperationalCapsFields = OperationalCaps.omit({
  currency: true,
  updatedAt: true,
  updatedBy: true,
});

export const setOperationalCaps = defineEndpoint({
  method: 'PUT',
  path: '/internal/caps',
  mount: 'internal',
  summary: 'Set basket, small-order and capacity ceilings; audited with before and after (OPS-005)',
  /*
   * PATCH semantics via `patchSchema` (../patch), not `.partial()`: omitted =
   * unchanged, and for the two NULLABLE fields - the small-order fee and its
   * threshold - an explicit `null` is the instruction to switch the fee off
   * (PAY-023). Three states on the wire, one merge rule on the server.
   */
  body: patchSchema(OperationalCapsFields),
  idempotent: true,
  response: OperationalCaps,
});

// ---------------------------------------------------------------------------
// Ordering modes (PRD OPS-025)
// ---------------------------------------------------------------------------

/**
 * What the platform SELLS, as two independent platform-wide switches (OPS-025).
 *
 * `preorder` is the scheduled morning the platform has always sold (GST-016);
 * `immediate` is an order for today, delivered as soon as a bakery and a
 * courier can. Both off is a legitimate state - the guest app renders the
 * ordering-closed screen and nobody can order anywhere.
 *
 * Deliberately NOT the kill switch: `servicePaused` (CON-008, OPS-009) stays
 * senior and says whether the platform is OPEN. These say what it sells.
 */
export const OrderingModes = z.object({
  preorder: z.boolean(),
  immediate: z.boolean(),
  updatedAt: Instant.nullable(),
  updatedBy: z.string().max(160).nullable(),
});
export type OrderingModes = z.infer<typeof OrderingModes>;

/**
 * GST-043: the two numbers the guest support page offers. Administered rather
 * than configured, because the printed card's small code and the app bar's
 * support icon both open a page that must change the day a number exists -
 * with no deploy, and on cards printed months earlier. Both null is the
 * starting state: the page then offers the support address alone.
 */
export const GuestSupportContact = z.object({
  whatsapp: PhoneE164.nullable(),
  phone: PhoneE164.nullable(),
  updatedAt: Instant.nullable(),
  updatedBy: z.string().max(160).nullable(),
});
export type GuestSupportContact = z.infer<typeof GuestSupportContact>;

export const getGuestSupportContact = defineEndpoint({
  method: 'GET',
  path: '/internal/guest-support',
  mount: 'internal',
  summary: 'The WhatsApp and phone numbers the guest support page offers (GST-043)',
  response: GuestSupportContact,
});

/** The administered numbers without the read-only envelope fields. */
export const GuestSupportContactFields = GuestSupportContact.omit({
  updatedAt: true,
  updatedBy: true,
});

export const setGuestSupportContact = defineEndpoint({
  method: 'PUT',
  path: '/internal/guest-support',
  mount: 'internal',
  summary: 'Set or clear the guest support numbers; audited with before and after (GST-043)',
  /* `patchSchema`: omitted = unchanged, null = cleared (the number is withdrawn
   * and the page stops offering it), a value sets. */
  body: patchSchema(GuestSupportContactFields),
  idempotent: true,
  response: GuestSupportContact,
});

/**
 * NTF-009: WhatsApp, as Internal > Settings shows it - whether it is on, who is
 * told about every new order, and what Meta decided about each template.
 *
 * `enabled` and `configured` are the instance's own configuration and cannot be
 * changed from here: switching a channel on that sends to guests is a deploy
 * decision (`WHATSAPP_ENABLED`), not a checkbox. The recipients ARE saved here,
 * because they change when a founder changes phone and must not need a deploy.
 */
export const WhatsAppTemplateState = z.object({
  name: z.string().max(512),
  /** Meta's language code: `en_US`, `fr`. */
  language: z.string().max(16),
  /** Meta's word (APPROVED, PENDING, REJECTED, PAUSED...) or NOT_SUBMITTED. */
  status: z.string().max(40),
  rejectedReason: z.string().max(400).nullable(),
});
export type WhatsAppTemplateState = z.infer<typeof WhatsAppTemplateState>;

export const WhatsAppSettings = z.object({
  /** WHATSAPP_ENABLED=1 and the sending credentials are both present. */
  enabled: z.boolean(),
  /** The sending credentials are present, whatever the switch says. */
  configured: z.boolean(),
  /** The business account id is set too, so templates can be submitted and read. */
  canManageTemplates: z.boolean(),
  /** Told on WhatsApp about every new order. Empty: nobody. */
  alertRecipients: z.array(PhoneE164).max(5),
  updatedAt: Instant.nullable(),
  updatedBy: z.string().max(160).nullable(),
  /** One row per template and language the platform sends. Empty when they cannot be read. */
  templates: z.array(WhatsAppTemplateState).max(100),
  /** Why the templates could not be read from Meta, when they could not. */
  templatesError: z.string().max(400).nullable(),
});
export type WhatsAppSettings = z.infer<typeof WhatsAppSettings>;

export const getWhatsAppSettings = defineEndpoint({
  method: 'GET',
  path: '/internal/whatsapp',
  mount: 'internal',
  summary: 'WhatsApp: on or off, new-order alert recipients, template approval (NTF-009)',
  response: WhatsAppSettings,
});

export const WhatsAppSettingsFields = z.object({ alertRecipients: z.array(PhoneE164).max(5) });

export const setWhatsAppSettings = defineEndpoint({
  method: 'PUT',
  path: '/internal/whatsapp',
  mount: 'internal',
  summary: 'Set who is told on WhatsApp about every new order; audited (NTF-009)',
  /* `patchSchema`: omitted = unchanged; a list REPLACES the list, and an empty
   * one tells nobody. */
  body: patchSchema(WhatsAppSettingsFields),
  idempotent: true,
  response: WhatsAppSettings,
});

export const submitWhatsAppTemplates = defineEndpoint({
  method: 'POST',
  path: '/internal/whatsapp/templates',
  mount: 'internal',
  summary: 'Submit to Meta every WhatsApp template it does not hold yet; audited (NTF-009)',
  body: z.object({}).optional(),
  idempotent: false,
  response: WhatsAppSettings.extend({
    submitted: z
      .array(
        z.object({
          name: z.string().max(512),
          language: z.string().max(16),
          outcome: z.enum(['SUBMITTED', 'REFUSED']),
          detail: z.string().max(400).nullable(),
        }),
      )
      .max(100),
  }),
});

export const getOrderingModes = defineEndpoint({
  method: 'GET',
  path: '/internal/ordering',
  mount: 'internal',
  summary: 'The two platform-wide ordering switches in force (OPS-025)',
  response: OrderingModes,
});

/** The administered switches without the read-only envelope fields. */
export const OrderingModesFields = OrderingModes.omit({
  updatedAt: true,
  updatedBy: true,
});

export const setOrderingModes = defineEndpoint({
  method: 'PUT',
  path: '/internal/ordering',
  mount: 'internal',
  summary:
    'Switch pre-order and immediate delivery on or off; audited with before and after (OPS-025)',
  /* PATCH semantics via `patchSchema` (../patch), exactly as the caps above:
   * omitted = unchanged, a value sets. Neither field is nullable, so the clear
   * case does not arise - but the merge rule is the platform's one rule. */
  body: patchSchema(OrderingModesFields),
  idempotent: true,
  response: OrderingModes,
});

// ---------------------------------------------------------------------------
// The delivery adapter the next booking uses (PRD RTE-023)
// ---------------------------------------------------------------------------

/**
 * Which delivery provider books the NEXT order, and which ones this deploy
 * could book through at all (RTE-023).
 *
 * `provider` is the EFFECTIVE name - what an order booked right now would
 * record - and it is NULL when nothing is configured: nobody has chosen, or
 * the choice names an adapter this deploy no longer holds. The setting alone
 * decides and there is no environment default under it (founder decision
 * 2026-09-29), so null means the platform refuses every new booking until an
 * operator chooses. `available` is the registry's own key list, never a set
 * written down twice, so a provider that is not configured cannot be offered.
 *
 * It moves the next booking and nothing else: an order that already holds a job
 * keeps the adapter written on its own row, so no courier is ever left riding
 * for a provider the platform has stopped addressing.
 *
 * `guestSms` sits beside it because it is a question about the courier
 * company (NTF-001, founder decision 2026-09-24): whether the platform texts
 * guests about their order at all. Off by default. Even on, an order Uber
 * Direct holds is never texted - Uber texts the guest itself - and a guest who
 * chose WhatsApp gets WhatsApp instead. `updatedAt`/`updatedBy` describe the
 * provider choice; a change to `guestSms` is in the audit log under its own
 * action.
 */
export const DeliveryProviderSetting = z.object({
  provider: z.string().max(40).nullable(),
  available: z.array(z.string().max(40)),
  guestSms: z.boolean(),
  updatedAt: Instant.nullable(),
  updatedBy: z.string().max(160).nullable(),
});
export type DeliveryProviderSetting = z.infer<typeof DeliveryProviderSetting>;

export const getDeliveryProvider = defineEndpoint({
  method: 'GET',
  path: '/internal/delivery-provider',
  mount: 'internal',
  summary: 'The delivery adapter the next booking goes to, and the ones configured (RTE-023)',
  response: DeliveryProviderSetting,
});

export const setDeliveryProvider = defineEndpoint({
  method: 'PUT',
  path: '/internal/delivery-provider',
  mount: 'internal',
  summary:
    'Choose the delivery adapter the next booking goes to, and whether guests are texted; audited with before and after',
  /* PATCH semantics via `patchSchema` (../patch), exactly as the ordering
   * switches above: omitted = unchanged, a value sets. The read-only fields
   * are not administered, so only `provider` and `guestSms` are offered - and
   * `provider` takes a NAME, never null: a choice can be changed, not cleared. */
  body: patchSchema(z.object({ provider: z.string().max(40), guestSms: z.boolean() })),
  idempotent: true,
  response: DeliveryProviderSetting,
});

// ---------------------------------------------------------------------------
// Self-serve monitoring and first-payout review (PRD OPS-017)
// ---------------------------------------------------------------------------

export const AnomalyKind = z.enum([
  /** Unusual QR-generation volume for a self-serve organization. */
  'QR_GENERATION_VOLUME',
  /** Orders concentrated on a single address. */
  'ADDRESS_CONCENTRATION',
]);
export type AnomalyKind = z.infer<typeof AnomalyKind>;

export const MonitoringThresholds = z.object({
  /** QR codes generated by one organization before it is flagged. */
  qrGenerationAlert: z.int().positive().max(100_000),
  /** Orders on one property in the observed window before it is flagged. */
  addressConcentrationAlert: z.int().positive().max(100_000),
  /** Days of history the two counters above are measured over. */
  observationWindowDays: z.int().positive().max(365),
  /**
   * Checkout refusals in one service day before an operations alert is raised
   * (NTF-003, OPS-027). Administered here rather than compiled in because the
   * number that means "something is wrong" is different for four apartments and
   * for four hundred, and moving it must not be a deploy.
   */
  checkoutRefusalsDailyAlert: z.int().positive().max(100_000),
  currency: CurrencyCode,
});
export type MonitoringThresholds = z.infer<typeof MonitoringThresholds>;

export const AnomalyAlert = z.object({
  id: Uuid,
  kind: AnomalyKind,
  organizationId: Uuid,
  organizationName: z.string().max(160),
  /** The measured value that tripped the threshold, and the threshold itself. */
  observed: z.int().nonnegative(),
  threshold: z.int().nonnegative(),
  /** The property the concentration was observed on, where the kind has one. */
  propertyId: Uuid.nullable(),
  detectedAt: Instant,
  detail: z.string().max(400),
  acknowledgedAt: Instant.nullable(),
  acknowledgedBy: z.string().max(160).nullable(),
});
export type AnomalyAlert = z.infer<typeof AnomalyAlert>;

export const getSelfServeMonitoring = defineEndpoint({
  method: 'GET',
  path: '/internal/monitoring',
  mount: 'internal',
  summary: 'Anomalous self-serve activity (OPS-017)',
  query: z.object({
    organizationId: Uuid.optional(),
    includeAcknowledged: z.boolean().default(false),
  }),
  response: z.object({
    thresholds: MonitoringThresholds,
    alerts: z.array(AnomalyAlert),
  }),
});

/** The administered thresholds without the read-only currency stamp. */
export const MonitoringThresholdFields = MonitoringThresholds.omit({ currency: true });

export const setMonitoringThresholds = defineEndpoint({
  method: 'PUT',
  path: '/internal/monitoring/thresholds',
  mount: 'internal',
  summary: 'Configure the self-serve alert thresholds (OPS-017)',
  // PATCH semantics via `patchSchema` (../patch): an omitted threshold keeps
  // its administered value; none is nullable, so there is no clear instruction.
  body: patchSchema(MonitoringThresholdFields),
  idempotent: true,
  response: MonitoringThresholds,
});

export const acknowledgeAnomaly = defineEndpoint({
  method: 'POST',
  path: '/internal/monitoring/alerts/:alertId/acknowledge',
  mount: 'internal',
  summary: 'Acknowledge an anomaly alert so it leaves the working list (OPS-017)',
  params: z.object({ alertId: Uuid }),
  /** The note is offered, never demanded: acknowledging is the decision. */
  body: z.object({ note: z.string().max(1000).optional() }),
  idempotent: true,
  response: AnomalyAlert,
});

// Service geography and catalogue configuration (PRD OPS-004, OPS-005, CAT-010)
// ---------------------------------------------------------------------------

/**
 * The service geography is DERIVED, not administered.
 *
 * It used to be a form: sectors, each an operator-drawn polygon with a city, a
 * timezone, a distance limit and a default bakery, stored and audited. Nothing
 * ever read one. The closest-source decision is made by `@croissants/allocation`
 * from a bakery's own coordinates and record and has never consulted a
 * sector, so the screen was administering a fact the product does not use -
 * while the fact it does use, which ground each bakery actually wins, was
 * invisible. What replaced it is this endpoint: the allocator, run at every
 * point of a lattice.
 */

/**
 * One run of the coverage grid, `[value, length]`.
 *
 * A tuple rather than an object because this array is most of the body; the
 * encoder and decoder are `encodeRuns`/`decodeRuns` in
 * `@croissants/contracts/projections`, shared by the worker that writes it and
 * the portal that draws it.
 */
export const CoverageRun = z.tuple([z.int(), z.int().positive()]);
export type CoverageRun = z.infer<typeof CoverageRun>;

/** One bakery on the map: where it is, how far it reaches, and its colour. */
export const CoverageBakery = z.object({
  organizationId: Uuid,
  locationId: Uuid,
  name: z.string().max(160),
  /**
   * The shop's street line and postcode, as one line for the legend to print
   * under the name. Composed server-side so the four surfaces that could show
   * a shop cannot each invent their own punctuation, and because it is what
   * tells two counters of the same house apart - the name on this row is the
   * house's.
   */
  address: z.string().max(240),
  /**
   * The arrondissement that address names, 1 to 20, or null when the postcode
   * names none (`parisArrondissement`). The legend orders by it, so the order
   * is a fact about the city rather than about the map: switching a shop off
   * changes what is drawn and never where a row sits.
   */
  arrondissement: z.int().min(1).max(20).nullable(),
  latitude: Latitude,
  longitude: Longitude,
  /**
   * The shop's service radius, drawn as a circle: operator information about
   * how much ground each shop covers within its cheaper delivery distance. It
   * decides nothing (founder, 2026-09-26). Null when none is on record.
   */
  serviceRadiusMetres: z.int().positive().nullable(),
  /**
   * Assigned SERVER-SIDE, from a fixed categorical order, so the map and its
   * legend cannot disagree about which shop is which colour. Two steps of the
   * same slot: `light` is validated against the portal's cream ground, `dark`
   * against a dark map canvas.
   */
  colour: z.object({
    light: z.string().regex(/^#[0-9a-f]{6}$/),
    dark: z.string().regex(/^#[0-9a-f]{6}$/),
  }),
  /**
   * False when the shop is in the pool but wins no ground at all - out of this
   * mode, inactive, or outranked everywhere. The legend still lists it, and
   * says why it is not on the map.
   */
  onMap: z.boolean(),
});
export type CoverageBakery = z.infer<typeof CoverageBakery>;

/** A conciergerie or an independent owner, and the colour its apartments wear. */
export const CoverageOwner = z.object({
  organizationId: Uuid,
  name: z.string(),
  type: PartnerOrgType,
  colour: z.string().regex(/^#[0-9a-f]{6}$/),
});
export type CoverageOwner = z.infer<typeof CoverageOwner>;

export const getCoverage = defineEndpoint({
  method: 'GET',
  path: '/internal/coverage',
  mount: 'internal',
  summary: 'The catchment each bakery actually wins, sampled from the allocator (OPS-004)',
  /**
   * Required, never defaulted. OPS-013 makes the two storefront pools exclusive,
   * so mode A and mode B are two genuinely different maps and there is no
   * sensible "both": a defaulted mode would silently show one of them under a
   * title that claims to be the service area.
   */
  query: z.object({
    storefrontMode: BakeryStorefrontMode,
    /**
     * Comma-separated location ids to leave OUT of the allocation, so the map
     * can answer "who takes this shop's ground if it goes". The allocator is
     * re-run without them; `bakeries` still lists every shop in the pool, so an
     * excluded one keeps its legend row - it just wins nothing and is marked
     * off the map. An id the pool does not contain is ignored, not an error:
     * the client echoes whatever the operator last toggled, and a shop that
     * left the pool between requests is not the operator's mistake.
     */
    exclude: z.string().max(4000).optional(),
  }),
  response: z.object({
    storefrontMode: BakeryStorefrontMode,
    computedAt: Instant,
    /**
     * South-west corner of cell 0. The lattice is row-major from here, a degree
     * of latitude is a fixed distance and a degree of longitude is that scaled
     * by cos(origin latitude) - so these four numbers describe every cell
     * without a reference latitude having to travel too (`@croissants/geo`).
     */
    originLatitude: Latitude,
    originLongitude: Longitude,
    /**
     * Actual pitch, which may be coarser than the 100 m the server aims for
     * when the derived extent is very large. Reported so the map never claims a
     * resolution it did not sample at.
     */
    cellSizeMetres: z.int().positive(),
    cols: z.int().nonnegative(),
    rows: z.int().nonnegative(),
    /**
     * Index into `bakeries` per cell; -1 where nothing is eligible INSIDE the
     * served market, and -2 outside it.
     *
     * The two negatives are not interchangeable and must not be drawn alike.
     * -1 is a gap: addressable ground the platform means to serve and does not.
     * -2 is Saint-Denis - not our market, nothing owed. Painted the same, every
     * map of Paris would appear ringed by an enormous unserved region.
     */
    winners: z.array(CoverageRun),
    /**
     * How many bakeries are eligible per cell. ALC-009 ("at least two eligible
     * bakeries") re-expressed over the derived map: a 1 is ground that one
     * closure takes out of service.
     */
    coverage: z.array(CoverageRun),
    bakeries: z.array(CoverageBakery),
    /**
     * Who the apartments belong to: every conciergerie and independent owner
     * with a dot on this map, each with the colour its dots are drawn in.
     *
     * The colour is assigned server-side and NEVER changes for an organization
     * (founder, 2026-09-20: "always keep the same color for each so we get used
     * to it") - the dots and the list under the map read this one array, so
     * they cannot disagree, and a client that joins or leaves moves nobody
     * else's colour. Order: conciergeries first, then independent owners, each
     * by name.
     */
    owners: z.array(CoverageOwner),
    /**
     * Every listed apartment: where it is, what it is called, its address, and
     * which of `owners` it belongs to (an index into that array).
     *
     * The catchment shows where the platform could deliver; these show where it
     * is being asked to, and the pair answers what neither does alone - whether
     * uncovered ground is empty or is where the apartments are. Until
     * 2026-09-20 a dot was a coordinate and nothing else; the founder asked for
     * the name and address on hover, outside every reach circle included,
     * because a dot nobody can identify cannot be acted on. Still no id and no
     * guest data: this is the apartment, never who is staying in it.
     */
    properties: z.array(
      z.object({
        latitude: Latitude,
        longitude: Longitude,
        name: z.string(),
        address: z.string(),
        owner: z.int().nonnegative(),
      }),
    ),
  }),
});

export const setSubstitutionMapping = defineEndpoint({
  method: 'POST',
  path: '/internal/catalogue/substitutions',
  mount: 'internal',
  summary: 'The only source of replacements offered on the tablet (CAT-010, BAK-019)',
  body: z.object({
    bakeryOrganizationId: Uuid,
    mappings: z.array(
      z.object({ itemId: PublicId, replacementItemIds: z.array(PublicId).max(10) }),
    ),
  }),
  idempotent: true,
  response: Ok,
});
